import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Send, Mail, MessageCircle, Linkedin, ScanLine } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/investor-leads`, formData);
      toast.success('Thank you! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#F3F0E6]">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#10B981]">Get Started</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#022C22] mt-4 mb-6">
              Request Investor Pack
            </h2>
            <p className="text-base leading-relaxed text-[#4B5563] mb-6">
              Fill out the form and our team will reach out with detailed documentation, financial projections, and next steps.
            </p>

            <div className="bg-white border-l-4 border-[#10B981] rounded-lg p-6 mb-6 shadow-sm">
              <h4 className="font-semibold text-[#022C22] mb-4 flex items-center">
                <Mail className="h-5 w-5 text-[#10B981] mr-2" />
                Contact Details
              </h4>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-[#4B5563] font-medium mb-1">Email:</p>
                  <a href="mailto:liveneat.organic@gmail.com" className="text-[#10B981] hover:text-[#059669] font-semibold transition-colors">
                    liveneat.organic@gmail.com
                  </a>
                </div>
                <div>
                  <p className="text-[#4B5563] font-medium mb-1 flex items-center">
                    <MessageCircle className="h-4 w-4 mr-1" />
                    WhatsApp:
                  </p>
                  <div className="space-y-1">
                    <a href="https://wa.me/918595672124" target="_blank" rel="noopener noreferrer" className="text-[#10B981] hover:text-[#059669] font-semibold transition-colors block">
                      +91 8595672124
                    </a>
                    <a href="https://wa.me/917411515444" target="_blank" rel="noopener noreferrer" className="text-[#10B981] hover:text-[#059669] font-semibold transition-colors block">
                      +91 74115 15444
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border-l-4 border-[#0A66C2] rounded-lg p-6 mb-6 shadow-sm">
              <h4 className="font-semibold text-[#022C22] mb-4 flex items-center">
                <Linkedin className="h-5 w-5 text-[#0A66C2] mr-2" />
                Founder Profile
              </h4>
              <div className="flex items-start gap-5">
                <div className="flex-1">
                  <p className="font-bold text-[#022C22] text-lg mb-1">Anupam Prasad</p>
                  <p className="text-sm text-[#4B5563] mb-3">Founder, LIVENEAT</p>
                  <a
                    href="https://www.linkedin.com/in/anupam-prasad-a0a249388"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#0A66C2] hover:bg-[#004182] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                    data-testid="linkedin-profile-link"
                  >
                    <Linkedin className="h-4 w-4" />
                    View on LinkedIn
                  </a>
                </div>
                <div className="flex-shrink-0 text-center">
                  <div className="bg-white border-2 border-[#0A66C2] rounded-xl p-2 shadow-md">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent('https://www.linkedin.com/in/anupam-prasad-a0a249388')}&color=0A66C2`}
                      alt="Scan to view LinkedIn profile"
                      className="w-[120px] h-[120px]"
                    />
                  </div>
                  <p className="text-xs font-semibold text-[#0A66C2] mt-2 flex items-center justify-center gap-1">
                    <ScanLine className="h-3 w-3" />
                    Scan for profile
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="bg-[#10B981] w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-1">1</div>
                <div>
                  <h4 className="font-semibold text-[#022C22]">Submit Your Interest</h4>
                  <p className="text-sm text-[#4B5563]">Complete the form with your details</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-[#10B981] w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-1">2</div>
                <div>
                  <h4 className="font-semibold text-[#022C22]">Receive Investment Pack</h4>
                  <p className="text-sm text-[#4B5563]">Get detailed documentation and contracts</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-[#10B981] w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-1">3</div>
                <div>
                  <h4 className="font-semibold text-[#022C22]">Start Earning</h4>
                  <p className="text-sm text-[#4B5563]">Deployment begins within 2 weeks</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-[#064E3B] text-white rounded-2xl shadow-xl p-8 md:p-12"
            data-testid="investor-contact-form"
          >
            <h3 className="text-2xl font-semibold mb-6">Contact Information</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[#F3F0E6] mb-2">
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-white/10 border-white/40 text-white placeholder:text-white/60 focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981]"
                  placeholder="Enter your name"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#F3F0E6] mb-2">
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-white/10 border-white/40 text-white placeholder:text-white/60 focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981]"
                  placeholder="your.email@example.com"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-[#F3F0E6] mb-2">
                  Phone Number *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-white/10 border-white/40 text-white placeholder:text-white/60 focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981]"
                  placeholder="+91 XXXXX XXXXX"
                  data-testid="contact-phone-input"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#F3F0E6] mb-2">
                  Message (Optional)
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="bg-white/10 border-white/40 text-white placeholder:text-white/60 focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] resize-none"
                  placeholder="Any specific questions?"
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-semibold py-6 text-lg"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? 'Sending...' : (
                  <>
                    <Send className="mr-2 h-5 w-5" />
                    Request Investor Pack
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
